/** River substrate and low-bank habitat, derived from the existing final
 * surface and RiverHit. No terrain edits, RNG, spatial lookup or allocations. */
import {fallInfluence} from './riverFalls.js';
import type {RiverHit} from './rivers.js';

export interface RiverBankSample {sediment:number;rock:number;riparian:number}
export const riverBankSample=():RiverBankSample=>({sediment:0,rock:0,riparian:0});
const clamp=(x:number):number=>Math.max(0,Math.min(1,x));
const smooth=(a:number,b:number,x:number):number=>{const t=clamp((x-a)/(b-a));return t*t*(3-2*t)};

/** h/ws use amplitude units; distance, current and the height gates use metres.
 * This local classifier knows no global coast/settlement state. PlanetSampler
 * applies final authority exclusions before publishing the three masks. */
export function sampleRiverBank(h:number,hit:RiverHit,amplitude:number,out:RiverBankSample):RiverBankSample{
 out.sediment=out.rock=out.riparian=0;
 if(!(amplitude>0&&hit.waterHalf>0&&hit.carveHalf>hit.waterHalf))return out;
 const above=(h-hit.ws)*amplitude,edge=Math.min(hit.carveHalf*2.2,hit.waterHalf+32);
 if(hit.dist>=edge||above<=-1.4||above>=11)return out;
 const side=Math.max(-1,Math.min(1,(hit.cross??0)/hit.waterHalf));
 const bend=Math.max(-1,Math.min(1,hit.bankTurn??0))*side,inner=Math.max(0,bend),outer=Math.max(0,-bend);
 const grade=Math.max(0,hit.slope*amplitude),energy=Math.max(smooth(.018,.16,grade),smooth(1.3,3.1,hit.speed));
 const fall=hit.fall?fallInfluence(hit.fall,hit.s):0;
 // The channel bed is never a painted sand blanket. Shallow exposed shelves
 // favor deposition inside quiet bends; cut banks and fall shoulders favor rock.
 const margin=smooth(hit.waterHalf*.48,hit.waterHalf*.95,hit.dist)*(1-smooth(hit.carveHalf*.85,edge,hit.dist));
 const shallow=smooth(-1.4,-.35,above)*(1-smooth(1.8,4.5,above));
 out.sediment=margin*shallow*(1-.9*energy)*(.68+.32*inner-.5*outer)*(1-fall);
 const rockHeight=smooth(-.8,.15,above)*(1-smooth(5,11,above));
 out.rock=margin*rockHeight*clamp(.08+.67*energy+outer*.40+fall*.72);
 // A hanging fall and its lip/pool cannot recruit terrestrial ground plants.
 // The near-water band is strictly dry; high disconnected cliffs fade out.
 out.riparian=margin*smooth(.08,.75,above)*(1-smooth(3,9,above))*(1-.65*energy)*(1-fall);
 return out;
}
